/* LumaSpec configuration picker (X3-PICKER lane) for quote-only ranges: snippets/ls-buy-box-picker.liquid renders
   the options from product.metafields.lsv.options; this wires them up the way the Pages site's picker does
   (src/site/site.js [data-vpick]): one value per option, a value that no configuration has together with the
   current choices is shown as unavailable, and picking it moves the other options to the nearest real configuration
   (the most options kept, catalog order on a tie). The chosen configuration's LS number, list price and options go
   into the buy box's quote item ([data-ls-quote-item], read by ls-product.js on Add to quote) as
   {v: '', sku: <configuration LS number>, o: {option: value}}: no variant id, so the quote request makes a text line
   with the exact LS number (the store holds one variant per range), priced by us - as on the Pages site.
   Contract: {n: [names], v: [[values]], p: 'LS-...-', c: [['<base-36 index per option>', '<suffix>', price]], w?} */

const B36 = '0123456789abcdefghijklmnopqrstuvwxyz';
const QMAX = 32; // the quote endpoint keeps at most 32 options per line
/* Interface strings + ui_map (display text of the English option names / values): snippets/ls-product-i18n.liquid,
   English defaults otherwise. The quote item keeps the English names and values. */
const STR = {
  changes: '(changes other options)',
  moved: 'Also changed to match an available configuration: {list}',
  na: 'n/a',
  ui_map: {},
};
try {
  Object.assign(STR, JSON.parse(document.querySelector('[data-ls-product-i18n]')?.textContent || '{}'));
} catch {
  /* keep the defaults */
}

/* the t filter HTML-escapes plain (non-_html) keys: back to plain text once (shown via textContent / esc()) */
for (const k of Object.keys(STR)) {
  if (typeof STR[k] === 'string' && STR[k].includes('&')) {
    const d = document.createElement('textarea');
    d.innerHTML = STR[k];
    STR[k] = d.value;
  }
}
const MAP = STR.ui_map && typeof STR.ui_map === 'object' ? STR.ui_map : {};
const show = (x) => (x ? MAP[x] || x : STR.na);
const money = (n) =>
  '$' + Number(n).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

function readJSON(el) {
  try {
    return JSON.parse((el && el.textContent) || 'null');
  } catch {
    return null;
  }
}

function initPicker(box) {
  if (box.dataset.lsReady) return;
  const d = readJSON(box.querySelector('[data-ls-pick-data]'));
  if (!d || !Array.isArray(d.n) || !Array.isArray(d.c) || !d.c.length) return;
  box.dataset.lsReady = '1';
  const w = d.w || 1;
  const rows = d.c.map((r) => {
    const idx = [];
    for (let i = 0; i < d.n.length; i++) {
      let x = 0;
      for (const ch of String(r[0]).slice(i * w, i * w + w)) x = x * 36 + B36.indexOf(ch);
      idx.push(x);
    }
    return { idx, sku: (d.p || '') + r[1], price: r[2] };
  });
  const pdp = box.closest('[data-ls-pdp]') || document;
  const opts = [...box.querySelectorAll('.ls-pick__opt')];
  const out = {
    sku: box.querySelector('[data-ls-pick-sku]'),
    price: box.querySelector('[data-ls-pick-price]'),
    msg: box.querySelector('[data-ls-pick-msg]'),
    legend: box.querySelector('[data-ls-pick-legend]'),
    now: pdp.querySelector('[data-ls-pick-now]'),
    from: pdp.querySelector('[data-ls-pick-from]'),
    item: pdp.querySelector('[data-ls-quote-item]'),
  };
  let cur = rows[0];

  // values of option i that exist together with the other current choices
  const possible = (i) => {
    const ok = new Set();
    for (const r of rows) if (r.idx.every((x, k) => k === i || x === cur.idx[k])) ok.add(r.idx[i]);
    return ok;
  };

  function paint() {
    let marked = false;
    opts.forEach((el, i) => {
      const ok = possible(i);
      const sel = el.querySelector('select');
      if (sel) {
        sel.value = String(cur.idx[i]);
        for (const o of sel.options) {
          const base = o.dataset.label || (o.dataset.label = o.textContent || '');
          o.textContent = ok.has(+o.value) ? base : `${base} \u21BB`;
          marked ||= !ok.has(+o.value);
        }
        return;
      }
      for (const b of el.querySelectorAll('[data-j]')) {
        const j = +(b.getAttribute('data-j') || 0);
        b.setAttribute('aria-pressed', String(j === cur.idx[i]));
        b.classList.toggle('is-x', !ok.has(j));
        const x = b.querySelector('[data-ls-pick-x]');
        if (x) x.hidden = ok.has(j);
        marked ||= !ok.has(j);
      }
    });
    if (out.legend) out.legend.hidden = !marked;
    if (out.sku) out.sku.textContent = cur.sku;
    // QA26b: the buy box's Model line and the spec table's Model row name the picked configuration's LS number
    for (const el of [...pdp.querySelectorAll('[data-ls-model]'), ...document.querySelectorAll('[data-ls-spec-model]')]) {
      el.textContent = cur.sku;
    }
    if (out.price && cur.price != null) out.price.textContent = money(cur.price);
    // the quote item: this configuration's LS number + its options (non-empty, range order, as the Pages site)
    const item = readJSON(out.item);
    if (item && out.item) {
      const o = {};
      d.n.forEach((name, i) => {
        const v = d.v[i][cur.idx[i]];
        if (v && Object.keys(o).length < QMAX) o[name] = v;
      });
      item.v = '';
      item.sku = cur.sku;
      item.o = o;
      out.item.textContent = JSON.stringify(item);
    }
  }

  // option i set to value j: that exact configuration, else the nearest one with value j
  function choose(i, j) {
    const want = cur.idx.slice();
    want[i] = j;
    let best = null;
    let score = -1;
    for (const r of rows) {
      if (r.idx[i] !== j) continue;
      const s = r.idx.reduce((n, x, k) => n + (x === want[k] ? 1 : 0), 0);
      if (s > score) [best, score] = [r, s];
    }
    if (!best) return;
    const moved = d.n.filter((_, k) => k !== i && best.idx[k] !== cur.idx[k]);
    cur = best;
    paint();
    if (out.now && cur.price != null) {
      out.now.textContent = money(cur.price); // a pick shows its own price; on load the page says "from" the lowest
      out.from?.remove();
    }
    if (out.msg) {
      out.msg.hidden = !moved.length;
      out.msg.textContent = moved.length
        ? STR.moved.replace(
            '{list}',
            () => moved.map((name) => `${show(name)}: ${show(d.v[d.n.indexOf(name)][cur.idx[d.n.indexOf(name)]])}`).join('; ')
          )
        : '';
    }
  }

  opts.forEach((el, i) => {
    const sel = el.querySelector('select');
    if (sel) sel.addEventListener('change', () => choose(i, +sel.value));
    el.addEventListener('click', (e) => {
      const b = e.target instanceof Element ? e.target.closest('[data-j]') : null;
      if (b) choose(i, +(b.getAttribute('data-j') || 0));
    });
  });
  paint();
}

const run = () => document.querySelectorAll('[data-ls-pick]').forEach((el) => initPicker(/** @type {HTMLElement} */ (el)));
run();
document.addEventListener('shopify:section:load', run);
